import * as React from 'react';
import { ReactNode, useState, useCallback, useEffect } from 'react';
import { ModalComponent } from '@SBGSports/catapult-core';
import { useTranslations } from 'i18n';
import styles from './wizard-modal.module.css';

export interface WizardStepButton {
    label: string;
    disabled?: boolean;
    action?: () => void | Promise<void>;
}

export interface WizardStepConfig {
    content: ReactNode | ((goToStep: (stepIndex: number) => void) => ReactNode);
    confirmButton?: WizardStepButton;
    cancelButton?: WizardStepButton | null;
    showCancelButton?: boolean;
    cancelButtonPosition?: 'left' | 'right';
    onConfirm?: () => void | Promise<void> | boolean | Promise<boolean>;
    onCancel?: () => void | Promise<void> | boolean | Promise<boolean>;
}

export interface WizardModalProps {
    isOpen: boolean;
    onClose: () => void;
    steps: WizardStepConfig[];
    size?: '3xl' | '7xl';
    className?: string;
    header?: ReactNode;
    onComplete?: () => void;
}

export const WizardModal: React.FC<WizardModalProps> = (props) => {
    const { isOpen, onClose, steps, size = '3xl', className, header, onComplete } = props;
    const { __ } = useTranslations();
    const [currentStep, setCurrentStep] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);

    const currentStepConfig = steps[currentStep];
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === steps.length - 1;

    // Reset to first step when modal closes
    const handleClose = useCallback(() => {
        setCurrentStep(0);
        setIsProcessing(false);
        onClose();
    }, [onClose]);

    // Reset state when modal is closed externally
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(0);
            setIsProcessing(false);
        }
    }, [isOpen]);

    const goToStep = useCallback(
        (stepIndex: number) => {
            if (stepIndex >= 0 && stepIndex < steps.length) {
                setCurrentStep(stepIndex);
            }
        },
        [steps.length],
    );

    const handleConfirm = useCallback(async () => {
        if (isProcessing) {
            return;
        }

        setIsProcessing(true);
        try {
            // Execute custom onConfirm handler if provided
            if (currentStepConfig.onConfirm) {
                const result = await currentStepConfig.onConfirm();
                // If onConfirm returns false, don't advance to next step
                if (result === false) {
                    setIsProcessing(false);
                    return;
                }
            }

            // Execute custom button action if provided
            if (currentStepConfig.confirmButton?.action) {
                await currentStepConfig.confirmButton.action();
            }

            // If last step, close modal and call onComplete
            if (isLastStep) {
                handleClose();
                onComplete?.();
            } else {
                // Otherwise, advance to next step
                setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
            }
        } finally {
            setIsProcessing(false);
        }
    }, [currentStepConfig, isLastStep, isProcessing, steps.length, handleClose, onComplete]);

    const handleCancel = useCallback(async () => {
        if (isProcessing) {
            return;
        }

        setIsProcessing(true);
        try {
            // Execute custom onCancel handler if provided
            let shouldProceed = true;
            if (currentStepConfig.onCancel) {
                const result = await currentStepConfig.onCancel();
                // If onCancel returns false, skip default navigation behavior
                if (result === false) {
                    shouldProceed = false;
                }
            }

            // Execute custom button action if provided
            if (currentStepConfig.cancelButton?.action) {
                await currentStepConfig.cancelButton.action();
            }

            // Only proceed with default navigation if not suppressed
            if (shouldProceed) {
                // If first step, close modal
                if (isFirstStep) {
                    handleClose();
                } else {
                    // Otherwise, go back to previous step
                    setCurrentStep((prev) => Math.max(prev - 1, 0));
                }
            }
        } finally {
            setIsProcessing(false);
        }
    }, [currentStepConfig, isFirstStep, isProcessing, handleClose]);

    const confirmLabel = currentStepConfig.confirmButton?.label ?? (isLastStep ? __('misc.finish') : __('misc.next'));
    const confirmDisabled = currentStepConfig.confirmButton?.disabled ?? false;

    const cancelLabel = currentStepConfig.cancelButton?.label ?? (isFirstStep ? __('misc.cancel') : __('misc.back'));
    const showCancelButton = currentStepConfig.showCancelButton !== false && currentStepConfig.cancelButton !== null;
    const cancelButtonPosition = currentStepConfig.cancelButtonPosition ?? 'right';
    const cancelAsLeftButton = showCancelButton && cancelButtonPosition === 'left';

    const renderContent = () => {
        const content = currentStepConfig.content;
        if (typeof content === 'function') {
            return content(goToStep);
        }
        return content;
    };

    return (
        <ModalComponent
            className={`${styles['wizard-modal']} ${className || ''}`}
            openModal={isOpen}
            handleClose={handleClose}
            handleCancel={handleCancel}
            handleConfirm={handleConfirm}
            customConfirmLabel={confirmLabel}
            customCancelLabel={cancelLabel}
            showCancelButton={showCancelButton && !cancelAsLeftButton}
            confirmDisabled={confirmDisabled || isProcessing}
            size={size}
            extraButton={
                cancelAsLeftButton
                    ? {
                          label: cancelLabel,
                          action: handleCancel,
                          color: 'ghost' as const,
                      }
                    : undefined
            }
        >
            <div className={styles['wizard-content']}>
                {header && <div className={styles['wizard-header']}>{header}</div>}
                {renderContent()}
            </div>
        </ModalComponent>
    );
};

export default WizardModal;
